import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Select, Modal, Input, Tag, Empty, Spin } from 'antd';
import {
  SearchOutlined, ReloadOutlined, UserOutlined, CalendarOutlined, FlagOutlined,
  CarOutlined, SwapOutlined, CheckCircleOutlined, HistoryOutlined, EditOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import config from '../../config';
import usePermissions from '../../hooks/usePermissions';
import { NdoeImage , messageErreur } from '../../components/ndoe';
import AssistantConfusion from './AssistantConfusion';
import './centreRegularisation.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/* ---------------------------------------------------------------------------
   Centre de régularisation V2 (retours proprio 15/07) — fiche 360° en MODE
   BROUILLON : les corrections (cliente, dates commande/livraison, et par
   ligne : statut, date de prise en charge, prix) s'accumulent SANS RIEN
   écrire ; « Valider » ouvre un récapitulatif avant → après (+ motif pour les
   dates) et seule la confirmation déclenche les écritures — toujours via les
   endpoints transactionnels existants (commandePut, regulariser-date avec ses
   verrous, correction de ligne avec les siens). Les ACTIONS opérationnelles
   (affecter, prise en charge du jour, résultat, réaffecter, clôturer) gardent
   leur modal immédiat À CONFIRMATION : une vente ne peut naître que du flux
   Résultat (vente + mouvement + créance).
--------------------------------------------------------------------------- */

const FILES_META = [
  ['sans_livreur', '⚠ Sans livreur', 'Jamais confiées à un livreur'],
  ['bloquees_anciennes', '⏳ Essais / échanges anciens', 'Sans mouvement depuis plusieurs jours'],
  ['a_cloturer', '🏁 Clôturables maintenant', 'Au moins une paire livrée, reste à solder'],
  ['reservations_retombees', '📦 Réservations retombées', 'Affectation expirée ou libérée, cliente en attente'],
];

// Issues d'une paire. C'est LE seul chemin qui engage le stock et l'argent :
// la vente, le mouvement de stock et la créance sont écrits ensemble, dans la
// même transaction. « Retour au magasin » EST l'annulation d'une livraison —
// le libellé le dit désormais explicitement : le proprio cherchait « annuler »
// et ne le trouvait pas (constat 16/07).
const OUTCOMES = [
  ['LIVREE', '🟢 Livrée et vendue'],
  ['EN_ESSAI', '🔵 Déposée à l’essai'],
  ['REPORTEE', '🟡 Reportée'],
  ['RETOUR_LIVRAISON', '🔴 Annulée — paire revenue au magasin'],
  ['RETOUR_POUR_ECHANGE', '🟠 Retour pour échange'],
];

// Ce que chaque issue DÉCLENCHE réellement, dit avant d'écrire. Le serveur
// applique ces règles (matrice de transition) ; ici on explique, on ne garde pas.
const EFFET_OUTCOME = {
  LIVREE: 'La vente sera enregistrée, la paire sortira définitivement du stock et la créance sera mise à jour.',
  EN_ESSAI: 'La paire reste réservée au nom de la cliente (elle est chez elle). Aucune vente n’est enregistrée tant que le choix n’est pas fait.',
  REPORTEE: 'La paire reste réservée et sortie : la course est simplement reportée.',
  RETOUR_LIVRAISON: 'C’est l’annulation de cette livraison : la paire est déclarée revenue au magasin et redevient disponible à la vente. Ne le faites que si la paire est physiquement rentrée.',
  RETOUR_POUR_ECHANGE: 'La paire revient au magasin dans le cadre d’un échange : le flux d’échange prend le relais.',
};

// Statuts corrigeables en brouillon (métadonnée, via la correction de ligne —
// PAS de vente créée ici : « Livrée » ne s'obtient que par l'action Résultat).
const STATUTS_CORRIGEABLES = [
  ['AFFECTEE', 'Affectée (ré-ouverture)'],
  ['PRISE_EN_CHARGE', 'Prise en charge'],
  ['EN_COURS', 'En cours'],
  ['REPORTEE', 'Reportée'],
  ['NON_PRISE_EN_CHARGE', 'Non-prise en charge'],
];

const STATUT_TONES = {
  AFFECTEE: 'blue', PRISE_EN_CHARGE: 'gold', EN_COURS: 'gold', REPORTEE: 'orange',
  LIVREE: 'green', ECHANGE_TERMINE: 'green', EN_ESSAI: 'geekblue',
  RETOUR_LIVRAISON: 'red', RETOUR_CLIENT: 'red', RETOUR_POUR_ECHANGE: 'orange',
  ECHANGE_EN_COURS: 'orange', ECHANGE_A_LIVRER: 'orange', NON_PRISE_EN_CHARGE: 'volcano',
};

const nowLocal = () => format(new Date(), "yyyy-MM-dd'T'HH:mm");
const money = (n) => `${Number(n || 0).toLocaleString('fr-FR')} $`;
const jour = (d) => { try { return format(new Date(d), 'yyyy-MM-dd'); } catch (e) { return ''; } };
const dtLocal = (d) => { try { return format(new Date(d), "yyyy-MM-dd'T'HH:mm"); } catch (e) { return ''; } };

const CentreRegularisation = () => {
  const { ready, can } = usePermissions();
  const user = useSelector((s) => s.user?.currentUser);
  const userId = user?.id;
  // Le routeur /api/mobile-delivery a sa PROPRE auth (Authorization: Bearer),
  // distincte de l'en-tête `token` global — même convention que le hub livreur.
  const headersHub = { Authorization: `Bearer ${user?.accessToken || user?.token || ''}` };

  const [files, setFiles] = useState(null);
  const [fileActive, setFileActive] = useState('sans_livreur');
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineOuverte, setTimelineOuverte] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [livreurs, setLivreurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null); // actions immédiates { type, ligne?, … }
  const [envoi, setEnvoi] = useState(false);
  const [debutAction, setDebutAction] = useState(null);
  const [echo, setEcho] = useState(null);

  // ------- BROUILLON (aucune écriture tant que non confirmé) -------
  // { cliente: {id, nom} | null, date_commande: 'yyyy-mm-dd' | null,
  //   date_livraison: 'yyyy-mm-dd' | null,
  //   lignes: { [id_detail_livraison]: { statut?, date_prise?, prix? } } }
  const brouillonVide = { cliente: null, date_commande: null, date_livraison: null, lignes: {} };
  const [brouillon, setBrouillon] = useState(brouillonVide);
  const [recap, setRecap] = useState(null); // { motifCode, motifTexte } quand ouvert
  const [regulConfig, setRegulConfig] = useState(null);
  // Assistant « confusion vendu/retourné » + dossiers reprenables.
  const [assistant, setAssistant] = useState(null);   // { idCommande } quand ouvert
  const [dossiers, setDossiers] = useState([]);
  const chargerDossiers = useCallback(async () => {
    try { const { data } = await axios.get(`${DOMAIN}/api/regularisation/dossiers`); setDossiers(data.dossiers || []); }
    catch (e) {
      // Une panne n'est pas « aucun dossier à reprendre » (10/08/2026). La
      // fonction voisine `chargerFiles` le disait déjà ; celle-ci se taisait.
      setDossiers([]);
      toast.error(messageErreur(e, 'les dossiers à reprendre'));
    }
  }, []);

  const chargerFiles = useCallback(async () => {
    try { const { data } = await axios.get(`${DOMAIN}/api/regularisation/files`); setFiles(data); }
    catch (e) { toast.error(e.response?.data?.error || 'Chargement des files impossible.'); }
  }, []);

  const chargerFiche = useCallback(async (id) => {
    setChargement(true); setTimeline(null); setTimelineOuverte(false);
    setBrouillon(brouillonVide);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/regularisation/commande/${id}`);
      setFiche(data);
    } catch (e) { toast.error(e.response?.data?.error || 'Fiche introuvable.'); }
    finally { setChargement(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chargerTimeline = useCallback(async (id) => {
    try { const { data } = await axios.get(`${DOMAIN}/api/commande/timeline/${id}`); setTimeline(data); }
    catch (e) { setTimeline({ evenements: [] }); }
  }, []);

  useEffect(() => { chargerFiles(); }, [chargerFiles]);
  useEffect(() => { chargerDossiers(); }, [chargerDossiers]);
  useEffect(() => {
    axios.get(`${DOMAIN}/api/regularisation/livreurs`).then(({ data }) => setLivreurs(data?.livreurs || [])).catch(() => {});
    axios.get(`${DOMAIN}/api/client`).then(({ data }) => setClients(Array.isArray(data) ? data : [])).catch(() => {});
    axios.get(`${DOMAIN}/api/commande/regul-config`).then(({ data }) => setRegulConfig(data)).catch(() => {});
  }, []);

  const rechercher = async () => {
    if (!q.trim()) { setResultats(null); return; }
    try { const { data } = await axios.get(`${DOMAIN}/api/regularisation/recherche`, { params: { q } }); setResultats(data.items || []); }
    catch (e) { toast.error(e.response?.data?.error || 'Recherche impossible.'); }
  };

  const apresAction = async (message) => {
    if (message) toast.success(message);
    setModal(null);
    if (fiche) {
      const id = fiche.commande.id_commande;
      await chargerFiche(id);
      try {
        const { data } = await axios.get(`${DOMAIN}/api/regularisation/commande/${id}/echo`, {
          params: { depuis: debutAction || (Date.now() - 15000) },
        });
        setEcho(data);
      } catch (e) { /* l'écho est un confort : jamais bloquant */ }
    }
    chargerFiles();
  };

  const erreurDe = (e, repli) => toast.error(e.response?.data?.error || e.response?.data?.message || repli);
  const ouvrirAction = (m) => { setDebutAction(Date.now()); setEcho(null); setModal(m); };

  /* ---------------- Brouillon : poser / retirer des modifications ---------- */

  const poserLigne = (idDl, champ, valeur) => {
    setBrouillon((b) => {
      const l = { ...(b.lignes[idDl] || {}) };
      if (valeur === undefined || valeur === null || valeur === '') delete l[champ];
      else l[champ] = valeur;
      const lignes = { ...b.lignes };
      if (Object.keys(l).length) lignes[idDl] = l; else delete lignes[idDl];
      return { ...b, lignes };
    });
  };

  const nbModifs = (brouillon.cliente ? 1 : 0)
    + (brouillon.date_commande ? 1 : 0)
    + (brouillon.date_livraison ? 1 : 0)
    + Object.values(brouillon.lignes).reduce((n, l) => n + Object.keys(l).length, 0);

  /* ---------------- Validation groupée (récap → confirmation) -------------- */

  const listeChangements = () => {
    if (!fiche) return [];
    const ch = [];
    if (brouillon.cliente) ch.push({ cible: 'Commande', quoi: 'Cliente', avant: fiche.commande.client, apres: brouillon.cliente.nom });
    if (brouillon.date_commande) ch.push({ cible: 'Commande', quoi: 'Date de commande', avant: jour(fiche.commande.date_commande), apres: brouillon.date_commande });
    if (brouillon.date_livraison) ch.push({ cible: 'Commande', quoi: 'Date de livraison (lignes livrées)', avant: '—', apres: brouillon.date_livraison });
    fiche.lignes.forEach((l) => {
      const b = brouillon.lignes[l.id_detail_livraison];
      if (!b) return;
      const nom = `${l.nom_produit || 'Ligne'}${l.taille ? ' P' + l.taille : ''}`;
      if (b.statut) ch.push({ cible: nom, quoi: 'Statut', avant: (l.statut_livraison || '—').replaceAll('_', ' '), apres: b.statut.replaceAll('_', ' ') });
      if (b.date_prise) ch.push({ cible: nom, quoi: 'Date de prise en charge', avant: l.date_prise_en_charge_effective ? format(new Date(l.date_prise_en_charge_effective), 'dd/MM HH:mm') : '—', apres: format(new Date(b.date_prise), 'dd/MM HH:mm') });
      if (b.prix !== undefined) ch.push({ cible: nom, quoi: 'Prix', avant: money(l.prix_livraison ?? l.prix), apres: money(b.prix) });
    });
    return ch;
  };

  const confirmerBrouillon = async () => {
    const datesModifiees = brouillon.date_commande || brouillon.date_livraison;
    if (datesModifiees) {
      if (!recap.motifCode) return toast.error('Choisissez le motif de la régularisation de date.');
      if (recap.motifCode === 'autre' && (recap.motifTexte || '').trim().length < 5) {
        return toast.error('Précisez le motif (au moins 5 caractères).');
      }
    }
    setEnvoi(true);
    setDebutAction(Date.now()); setEcho(null);
    const c = fiche.commande;
    let ecritures = 0;
    // Motif en clair, partagé par les dates ET les corrections de ligne.
    const motifLisible = recap.motifCode === 'autre'
      ? (recap.motifTexte || '').trim()
      : ((regulConfig?.motifs || []).find((m) => m.code === recap.motifCode)?.label || recap.motifCode || '');
    try {
      // 1) Cliente (tuple complet : le PUT écrit tous les champs).
      if (brouillon.cliente) {
        await axios.put(`${DOMAIN}/api/commande/commandePut/${c.id_commande}`, {
          id_client: brouillon.cliente.id, statut: c.statut, id_livraison: c.id_livraison,
          id_paiement: c.id_paiement, id_shop: c.id_shop, paye: c.paye, retour: c.retour,
          user_cr: userId,
        });
        ecritures += 1;
      }
      // 2) Dates (endpoint dédié, verrous serveur : plancher, vendue, invariants).
      if (datesModifiees) {
        await axios.put(`${DOMAIN}/api/commande/regulariser-date/${c.id_commande}`, {
          date_commande: brouillon.date_commande || jour(c.date_commande),
          ...(brouillon.date_livraison ? { date_livraison: brouillon.date_livraison } : {}),
          motif_code: recap.motifCode,
          ...(recap.motifCode === 'autre' ? { motif: recap.motifTexte.trim() } : {}),
        });
        ecritures += 1;
      }
      // 3) Lignes (une correction transactionnelle par ligne modifiée).
      for (const [idDl, b] of Object.entries(brouillon.lignes)) {
        await axios.put(`${DOMAIN}/api/mobile-delivery/admin/line/${idDl}`, {
          ...(b.statut ? { statut: b.statut } : {}),
          ...(b.prix !== undefined ? { prix: Number(b.prix) } : {}),
          ...(b.date_prise ? { date_prise_en_charge: b.date_prise } : {}),
          // Le motif du récap était collecté puis JETÉ (il ne servait qu'aux
          // dates) : le journal des corrections de ligne n'avait donc jamais de
          // « pourquoi ». Le serveur l'enregistre désormais (correctif R4).
          ...(motifLisible ? { motif: motifLisible } : {}),
        }, { headers: headersHub });
        ecritures += 1;
      }
      toast.success(`${ecritures} correction(s) validée(s).`);
      setRecap(null);
      setBrouillon(brouillonVide);
      await apresAction(null);
    } catch (e) {
      erreurDe(e, 'Correction refusée.');
      if (ecritures > 0) {
        toast.warning(`${ecritures} correction(s) déjà écrite(s) avant l'erreur — la fiche va se recharger.`);
        setRecap(null); setBrouillon(brouillonVide);
        await apresAction(null);
      }
    } finally { setEnvoi(false); }
  };

  /* ---------------- Actions immédiates (modals à confirmation) ------------- */

  const lancerAffectation = async ({ force = false } = {}) => {
    const { ligne, livreurId, qte } = modal;
    if (!livreurId) return toast.error('Choisissez un livreur.');
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/livraison/affectation`, {
        id_commande: fiche.commande.id_commande,
        id_livreur: livreurId,
        id_user_cr: userId,
        lines: [{ id_detail_commande: ligne.id_detail, qte: Number(qte) || 1 }],
        force,
      });
      await apresAction(`Mission ${data.numero || ''} : paire affectée.`);
    } catch (e) {
      const resp = e.response?.data;
      if (e.response?.status === 409 && Array.isArray(resp?.blocages)) {
        setModal((m) => ({ ...m, blocages: resp.blocages }));
      } else { erreurDe(e, "Affectation refusée."); }
    } finally { setEnvoi(false); }
  };

  const lancerPriseEnCharge = async (ligne) => {
    setDebutAction(Date.now()); setEcho(null);
    setEnvoi(true);
    try {
      await axios.put(`${DOMAIN}/api/mobile-delivery/mine/${fiche.commande.id_commande}/take-charge`, {
        effective_at: nowLocal(), comment: 'Prise en charge depuis le Centre de régularisation',
        lines: [{ id_detail_livraison: ligne.id_detail_livraison, taken: true }],
        id_livreur: Number(ligne.id_livreur),
      }, { headers: headersHub });
      await apresAction('Prise en charge enregistrée.');
    } catch (e) { erreurDe(e, 'Prise en charge refusée.'); }
    finally { setEnvoi(false); }
  };

  const lancerResultat = async () => {
    const { ligne, outcome, quand, paiement, commentaire } = modal;
    if (!outcome) return toast.error('Choisissez le résultat.');
    setEnvoi(true);
    try {
      await axios.put(`${DOMAIN}/api/mobile-delivery/mine/${fiche.commande.id_commande}/result`, {
        effective_at: quand || nowLocal(),
        comment: commentaire || '',
        payment: outcome === 'LIVREE' ? Number(paiement) || 0 : 0,
        lines: [{ id_detail_livraison: ligne.id_detail_livraison, outcome }],
        id_livreur: Number(ligne.id_livreur),
      }, { headers: headersHub });
      await apresAction('Résultat enregistré.');
    } catch (e) { erreurDe(e, 'Résultat refusé.'); }
    finally { setEnvoi(false); }
  };

  const lancerReaffectation = async () => {
    const { ligne, livreurId, motif } = modal;
    if (!livreurId) return toast.error('Choisissez le nouveau livreur.');
    if (!motif || motif.trim().length < 5) return toast.error('Motif obligatoire (au moins 5 caractères).');
    setEnvoi(true);
    try {
      await axios.put(`${DOMAIN}/api/mobile-delivery/admin/reassign`, {
        id_detail_livraisons: [ligne.id_detail_livraison],
        id_livreur: livreurId,
        motif: motif.trim(),
      }, { headers: headersHub });
      await apresAction('Paire réaffectée.');
    } catch (e) { erreurDe(e, 'Réaffectation refusée.'); }
    finally { setEnvoi(false); }
  };

  const lancerCloture = async () => {
    const { motif } = modal;
    if (!motif || motif.trim().length < 5) return toast.error('Motif obligatoire (au moins 5 caractères).');
    setEnvoi(true);
    try {
      const { data } = await axios.put(`${DOMAIN}/api/commande/cloturer/${fiche.commande.id_commande}`, { motif: motif.trim() });
      await apresAction(data.message || 'Commande clôturée.');
    } catch (e) { erreurDe(e, 'Clôture refusée.'); }
    finally { setEnvoi(false); }
  };

  /* ------------------------------- Rendu ----------------------------------- */

  if (ready && !can('regularisation.acceder')) {
    return (
      <div className="creg-refus">
        <h2>Centre de régularisation</h2>
        <p>Accès réservé. Demandez le droit « Centre de régularisation » à l'administrateur
          (Administration → Matrice des permissions).</p>
      </div>
    );
  }

  const items = resultats !== null ? resultats : (files?.[fileActive]?.items || []);

  const renderItem = (it) => (
    <button key={it.id_commande} type="button" className={`creg-item${fiche?.commande?.id_commande === it.id_commande ? ' actif' : ''}`}
      onClick={() => { setEcho(null); chargerFiche(it.id_commande); }}>
      <b>#{it.id_commande} · {it.client}</b>
      <span>{it.paires} paire(s) · {money(it.montant)} · il y a {it.age_jours} j</span>
    </button>
  );

  const renderActions = (l) => {
    const a = l.actions || {};
    return (
      <div className="creg-ligne-actions">
        {a.affecter && <button type="button" onClick={() => ouvrirAction({ type: 'affecter', ligne: l, qte: l.quantite, livreurId: null })}><CarOutlined /> Affecter</button>}
        {a.prise_en_charge && <button type="button" disabled={envoi} onClick={() => lancerPriseEnCharge(l)}><CheckCircleOutlined /> Prise en charge</button>}
        {/* « Résultat / annuler » : c'est ici — et NULLE PART ailleurs — qu'on
            annule une livraison (issue « paire revenue au magasin »), parce que
            c'est le seul chemin qui rend le stock proprement. Le libellé le dit,
            sinon on la cherche dans le brouillon (qui ne l'offre pas, à raison). */}
        {a.resultat && <button type="button" title="Enregistrer le résultat de la paire — livrée, à l'essai, reportée, ou annulée (retour au magasin)" onClick={() => ouvrirAction({ type: 'resultat', ligne: l, outcome: '', quand: nowLocal(), paiement: Number(l.prix_livraison ?? l.prix) || 0, commentaire: '' })}><FlagOutlined /> Résultat / annuler</button>}
        {a.reaffecter && <button type="button" onClick={() => ouvrirAction({ type: 'reaffecter', ligne: l, livreurId: null, motif: '' })}><SwapOutlined /> Réaffecter</button>}
      </div>
    );
  };

  // Champs de correction d'une ligne (écrivent le BROUILLON, rien d'autre).
  const renderCorrections = (l) => {
    if (!l.id_detail_livraison) return null; // pas de livraison : rien à corriger ici
    const b = brouillon.lignes[l.id_detail_livraison] || {};
    const vendue = Number(l.qte_vendue) > 0;
    return (
      <div className="creg-ligne-corr">
        <label>
          <span>Statut</span>
          {/* Cette liste ne contient QUE des statuts sans effet sur le stock ni
              l'argent — c'est voulu, et le serveur l'impose désormais (matrice de
              transition). « Livrée » et « Annulée » passent par Résultat, seul
              chemin qui écrit vente + mouvement + créance ensemble. Le title le
              dit, sinon on cherche l'annulation ici. */}
          <Select size="small" style={{ minWidth: 190 }} placeholder={vendue ? 'Paire vendue' : 'Corriger…'}
            value={b.statut} allowClear disabled={vendue}
            title={vendue
              ? 'Paire vendue : passez par « Résultat / annuler »'
              : 'Corrections sans effet sur le stock ni l’argent. Pour livrer ou annuler la livraison, utilisez « Résultat / annuler ».'}
            onChange={(v) => poserLigne(l.id_detail_livraison, 'statut', v)}
            options={STATUTS_CORRIGEABLES.filter(([v]) => v !== l.statut_livraison).map(([v, lab]) => ({ value: v, label: lab }))} />
        </label>
        <label>
          <span>Prise en charge</span>
          <Input size="small" type="datetime-local" style={{ width: 185 }}
            value={b.date_prise !== undefined ? b.date_prise : (l.date_prise_en_charge_effective ? dtLocal(l.date_prise_en_charge_effective) : '')}
            onChange={(e) => {
              const v = e.target.value;
              const actuel = l.date_prise_en_charge_effective ? dtLocal(l.date_prise_en_charge_effective) : '';
              poserLigne(l.id_detail_livraison, 'date_prise', v && v !== actuel ? v : undefined);
            }} />
        </label>
        <label>
          <span>Prix ($)</span>
          <Input size="small" type="number" min={0} style={{ width: 90 }}
            value={b.prix !== undefined ? b.prix : Number(l.prix_livraison ?? l.prix) || 0}
            onChange={(e) => {
              const v = e.target.value;
              const actuel = String(Number(l.prix_livraison ?? l.prix) || 0);
              poserLigne(l.id_detail_livraison, 'prix', v !== '' && v !== actuel ? v : undefined);
            }} />
        </label>
        {!!Object.keys(b).length && <Tag color="gold">modifié</Tag>}
      </div>
    );
  };

  return (
    <div className="creg-page">
      <h1>Centre de régularisation</h1>
      {dossiers.length > 0 && (
        <div className="creg-dossiers">
          <b>📂 Régularisations en cours :</b>
          {dossiers.map((d) => (
            <button key={d.id_dossier} type="button" className="creg-dossier-chip"
              onClick={() => { chargerFiche(d.id_commande); setAssistant({ idCommande: d.id_commande }); }}>
              #{d.id_commande} · {d.cliente || 'cliente'} <span>reprendre →</span>
            </button>
          ))}
        </div>
      )}
      <div className="creg-layout">
        <aside className="creg-colonne">
          <div className="creg-recherche">
            <Input value={q} onPressEnter={rechercher}
              onChange={(e) => { setQ(e.target.value); if (!e.target.value) setResultats(null); }}
              placeholder="Code, cliente ou téléphone…" prefix={<SearchOutlined />} allowClear />
            <button type="button" onClick={rechercher}>Chercher</button>
          </div>
          {resultats !== null ? (
            <div className="creg-file-liste">
              <div className="creg-file-titre">Résultats ({resultats.length}) <button type="button" onClick={() => setResultats(null)}>× files</button></div>
              {resultats.map((it) => renderItem(it))}
              {!resultats.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune commande" />}
            </div>
          ) : (
            <>
              <div className="creg-files">
                {FILES_META.map(([cle, label]) => (
                  <button key={cle} type="button" className={`creg-file${fileActive === cle ? ' actif' : ''}`}
                    onClick={() => setFileActive(cle)}>
                    {label} <b>{files ? files[cle]?.n ?? 0 : '…'}</b>
                  </button>
                ))}
                <button type="button" className="creg-refresh" onClick={chargerFiles} title="Rafraîchir"><ReloadOutlined /></button>
              </div>
              <div className="creg-file-liste">
                <div className="creg-file-titre">{FILES_META.find(([c]) => c === fileActive)?.[2]}</div>
                {(items || []).map((it) => renderItem(it))}
                {files && !items.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="File vide 🎉" />}
              </div>
            </>
          )}
        </aside>

        <section className="creg-fiche">
          {chargement && <div className="creg-centre"><Spin /></div>}
          {!chargement && !fiche && (
            <div className="creg-vide">
              <p>Choisis une commande dans une file ou via la recherche :<br />
                tout se corrige ici — cliente, dates, statuts, prix — puis un seul bouton Valider.</p>
            </div>
          )}
          {!chargement && fiche && (
            <>
              <header className="creg-entete">
                <div>
                  <h2>Commande #{fiche.commande.id_commande}
                    <Tag color={Number(fiche.commande.statut) === 3 ? 'default' : (fiche.commande.nom_statut === 'Validé' ? 'green' : 'red')}>
                      {fiche.commande.nom_statut}
                    </Tag>
                  </h2>
                  <button type="button" className="creg-assistant-btn"
                    onClick={() => setAssistant({ idCommande: fiche.commande.id_commande })}>
                    🧩 Régulariser une confusion vendu/retourné
                  </button>
                  <div className="creg-entete-infos">
                    <span><UserOutlined />
                      <Select size="small" showSearch optionFilterProp="label" style={{ minWidth: 210 }}
                        value={brouillon.cliente ? brouillon.cliente.id : fiche.commande.id_client}
                        onChange={(v, opt) => setBrouillon((b) => ({
                          ...b,
                          cliente: v === fiche.commande.id_client ? null : { id: v, nom: opt.label.split(' · ')[0] },
                        }))}
                        options={clients.map((cl) => ({ value: cl.id, label: `${cl.nom}${cl.telephone ? ' · ' + cl.telephone : ''}` }))} />
                      {brouillon.cliente && <Tag color="gold">modifiée</Tag>}
                    </span>
                    <span><CalendarOutlined /> Commande&nbsp;
                      <Input size="small" type="date" style={{ width: 140 }}
                        value={brouillon.date_commande || jour(fiche.commande.date_commande)}
                        onChange={(e) => setBrouillon((b) => ({
                          ...b,
                          date_commande: e.target.value && e.target.value !== jour(fiche.commande.date_commande) ? e.target.value : null,
                        }))} />
                      {brouillon.date_commande && <Tag color="gold">modifiée</Tag>}
                      &nbsp;Livraison&nbsp;
                      <Input size="small" type="date" style={{ width: 140 }} placeholder="—"
                        title="Régularise la date de TOUTES les lignes livrées (même visite)"
                        value={brouillon.date_livraison || ''}
                        onChange={(e) => setBrouillon((b) => ({ ...b, date_livraison: e.target.value || null }))} />
                      {brouillon.date_livraison && <Tag color="gold">modifiée</Tag>}
                    </span>
                  </div>
                </div>
                <div className="creg-montants">
                  <div><small>Attendu</small><b>{money(fiche.commande.montant_attendu)}</b></div>
                  <div><small>Validé</small><b>{money(fiche.commande.montant_valide)}</b></div>
                  {fiche.dette && (
                    <div><small>Créance</small>
                      <b>{money(Math.max(0, (fiche.dette.montant_convenu || 0) - (fiche.dette.montant_paye || 0)))}</b>
                    </div>
                  )}
                </div>
              </header>

              {echo && (echo.evenements?.length || echo.mouvements?.length || echo.ventes?.length || echo.paiements?.length) ? (
                <div className="creg-echo">
                  <div className="creg-echo-titre">
                    <b>✅ Opérations effectuées par votre action</b>
                    <button type="button" onClick={() => setEcho(null)}>×</button>
                  </div>
                  <ul>
                    {(echo.evenements || []).map((e, i) => (
                      <li key={`e${i}`}>📋 {e.libelle}{e.livreur ? ` — livreur ${e.livreur}` : ''}{e.motif ? ` · motif : ${e.motif}` : ''}</li>
                    ))}
                    {(echo.mouvements || []).map((m, i) => (
                      <li key={`m${i}`}>📦 Mouvement stock « {m.type || '?'} » : {m.nom_produit || 'produit'}{m.taille ? ` P${m.taille}` : ''} ×{m.quantite}
                        {m.stock_avant !== null && m.stock_apres !== null ? ` — stock ${m.stock_avant} → ${m.stock_apres}` : ''}</li>
                    ))}
                    {(echo.ventes || []).map((v, i) => (
                      <li key={`v${i}`}>💵 Vente confirmée : {v.quantite} × {money(v.prix_unitaire)}{v.nom_produit ? ` (${v.nom_produit})` : ''}</li>
                    ))}
                    {(echo.paiements || []).map((p, i) => (
                      <li key={`p${i}`}>💰 {Number(p.montant) < 0 ? 'Contre-passation' : 'Paiement encaissé'} : {money(Math.abs(p.montant))}{p.moyen ? ` (${p.moyen})` : ''}</li>
                    ))}
                    {echo.dette && (
                      <li key="d">🧾 Créance de la cliente après l'action : {money(Math.max(0, (echo.dette.montant_convenu || 0) - (echo.dette.montant_paye || 0)))}</li>
                    )}
                  </ul>
                </div>
              ) : null}

              <div className="creg-lignes">
                {fiche.lignes.map((l) => (
                  <div key={l.id_detail} className={`creg-ligne${brouillon.lignes[l.id_detail_livraison] ? ' modifiee' : ''}`}>
                    <div className="creg-ligne-haut">
                      <NdoeImage src={l.img} alt={l.nom_produit || 'paire'} size="lg" />
                      <div className="creg-ligne-produit">
                        <b>{l.nom_produit || 'Produit'}</b>
                        <span>{l.code_produit || ''} · P{l.taille || '?'} · {l.quantite} paire(s) · {money(l.prix)}
                          {Number(l.qte_vendue) > 0 && <Tag color="green" style={{ marginLeft: 6 }}>{l.qte_vendue} vendue(s)</Tag>}
                        </span>
                        <div className="creg-ligne-etat">
                          {l.statut_livraison
                            ? <Tag color={STATUT_TONES[l.statut_livraison] || 'default'}>{l.statut_livraison.replaceAll('_', ' ')}</Tag>
                            : <Tag>SANS LIVRAISON</Tag>}
                          {l.livreur && <span className="creg-livreur"><CarOutlined /> {l.livreur}</span>}
                          {l.date_resultat && <small>{format(new Date(l.date_resultat), 'dd/MM HH:mm')}</small>}
                        </div>
                      </div>
                      {renderActions(l)}
                    </div>
                    {renderCorrections(l)}
                  </div>
                ))}
              </div>

              <footer className="creg-pied">
                {fiche.cloture.possible && (
                  <button type="button" className="creg-btn-cloture"
                    onClick={() => ouvrirAction({ type: 'cloturer', motif: '' })}>
                    <FlagOutlined /> Clôturer ({fiche.cloture.soldables} paire(s) à solder
                    {fiche.cloture.resultat_si_cloture === 'annulee' ? ' → commande ANNULÉE' : ' → reste Livrée'})
                  </button>
                )}
                {fiche.cloture.bloquantes > 0 && (
                  <span className="creg-note">⚠ {fiche.cloture.bloquantes} paire(s) en essai/échange : à résoudre avant clôture.</span>
                )}
                <button type="button" className="creg-lien creg-timeline-btn" onClick={() => {
                  setTimelineOuverte((v) => !v);
                  if (!timeline) chargerTimeline(fiche.commande.id_commande);
                }}>
                  <HistoryOutlined /> Historique
                </button>
              </footer>

              {timelineOuverte && (
                <div className="creg-timeline">
                  {!timeline ? <Spin size="small" /> : (timeline.evenements || []).map((e, i) => (
                    <div key={i} className="creg-tl-item">
                      <small>{e.date ? format(new Date(e.date), 'dd/MM HH:mm') : ''}</small>
                      <span>
                        <b>{e.libelle}</b>
                        {e.detail ? ` — ${e.detail}` : ''}
                        {e.montant !== undefined ? ` · ${money(e.montant)}` : ''}
                        {e.acteur ? ` · par ${e.acteur}` : ''}{e.livreur ? ` · livreur ${e.livreur}` : ''}
                      </span>
                    </div>
                  ))}
                  {timeline && !(timeline.evenements || []).length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun événement" />}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* -------- Barre de validation du brouillon (rien n'est écrit avant) --- */}
      {nbModifs > 0 && (
        <div className="creg-barre-valider">
          <span><EditOutlined /> {nbModifs} modification(s) en attente — rien n'est encore enregistré</span>
          <div>
            {/* « Tout annuler » a induit le proprio en erreur : il a cru annuler
                une LIVRAISON alors que ce bouton vide seulement le brouillon.
                Le mot « annuler » ne doit désigner qu'une annulation métier. */}
            <button type="button" className="creg-btn-annuler" onClick={() => setBrouillon(brouillonVide)}>Abandonner mes modifications</button>
            <button type="button" className="creg-btn-valider" onClick={() => setRecap({ motifCode: undefined, motifTexte: '' })}>
              Valider
            </button>
          </div>
        </div>
      )}

      {/* -------- Récapitulatif de confirmation -------- */}
      <Modal open={!!recap} title="Confirmer les corrections" onCancel={() => setRecap(null)}
        onOk={confirmerBrouillon} okText="Confirmer et enregistrer" cancelText="Revenir"
        confirmLoading={envoi} destroyOnClose width={560}>
        {recap && (
          <div className="creg-modal">
            <table className="creg-recap">
              <thead><tr><th>Cible</th><th>Champ</th><th>Avant</th><th>Après</th></tr></thead>
              <tbody>
                {listeChangements().map((c, i) => (
                  <tr key={i}><td>{c.cible}</td><td>{c.quoi}</td><td>{c.avant}</td><td><b>{c.apres}</b></td></tr>
                ))}
              </tbody>
            </table>
            {(brouillon.date_commande || brouillon.date_livraison) && (
              <>
                <label>Motif de la régularisation de date (obligatoire)</label>
                <Select style={{ width: '100%' }} placeholder="Choisir…" value={recap.motifCode}
                  onChange={(v) => setRecap((r) => ({ ...r, motifCode: v }))}
                  options={(regulConfig?.motifs || []).map((m) => ({ value: m.code, label: m.label }))} />
                {recap.motifCode === 'autre' && (
                  <Input.TextArea rows={2} placeholder="Précisez le motif…" value={recap.motifTexte}
                    onChange={(e) => setRecap((r) => ({ ...r, motifTexte: e.target.value }))} />
                )}
              </>
            )}
            <p className="creg-note">Chaque correction passe par les contrôles du système (verrous de dates,
              paires vendues protégées, prix propagé aux ventes et créances). Le panneau
              « Opérations effectuées » s'affichera après enregistrement.</p>
          </div>
        )}
      </Modal>

      {/* ------------------------------ Modals actions ------------------------ */}
      <Modal open={modal?.type === 'affecter'} title="Affecter à un livreur" onCancel={() => setModal(null)}
        onOk={() => lancerAffectation()} okText="Affecter" confirmLoading={envoi} destroyOnClose>
        {modal?.type === 'affecter' && (
          <div className="creg-modal">
            <label>Livreur</label>
            <Select style={{ width: '100%' }} placeholder="Choisir…" value={modal.livreurId}
              onChange={(v) => setModal((m) => ({ ...m, livreurId: v }))}
              options={livreurs.map((lv) => ({ value: lv.id, label: lv.nom || lv.username }))} />
            <label>Quantité (max {modal.ligne.quantite})</label>
            <Input type="number" min={1} max={modal.ligne.quantite} value={modal.qte}
              onChange={(e) => setModal((m) => ({ ...m, qte: e.target.value }))} />
            {modal.blocages && (
              <div className="creg-blocage">
                ⚠ Paire déjà réservée ailleurs : {modal.blocages.map((b) => b.libelle || b.detail || `commande #${b.id_commande}`).join(' ; ')}
                <button type="button" disabled={envoi} onClick={() => lancerAffectation({ force: true })}>Forcer quand même</button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={modal?.type === 'resultat'} title="Résultat de la paire (ou annulation)" onCancel={() => setModal(null)}
        onOk={lancerResultat} okText="Enregistrer" confirmLoading={envoi} destroyOnClose>
        {modal?.type === 'resultat' && (
          <div className="creg-modal">
            <label>Résultat</label>
            <Select style={{ width: '100%' }} placeholder="Choisir…" value={modal.outcome || undefined}
              onChange={(v) => setModal((m) => ({ ...m, outcome: v }))}
              options={OUTCOMES.map(([v, l]) => ({ value: v, label: l }))} />
            {/* Chaque issue engage le stock et l'argent : on dit CE QUI VA SE
                PASSER avant d'écrire, plutôt que de laisser deviner. */}
            {modal.outcome && (
              <p className="creg-effet">{EFFET_OUTCOME[modal.outcome]}</p>
            )}
            {modal.outcome === 'LIVREE' && (<>
              <label>Montant encaissé ($) — 0 = à crédit</label>
              <Input type="number" min={0} value={modal.paiement}
                onChange={(e) => setModal((m) => ({ ...m, paiement: e.target.value }))} />
            </>)}
            <label>Date et heure réelles</label>
            <Input type="datetime-local" value={modal.quand}
              onChange={(e) => setModal((m) => ({ ...m, quand: e.target.value }))} />
            <label>Commentaire (facultatif)</label>
            <Input value={modal.commentaire} onChange={(e) => setModal((m) => ({ ...m, commentaire: e.target.value }))} />
          </div>
        )}
      </Modal>

      <Modal open={modal?.type === 'reaffecter'} title="Réaffecter à un autre livreur" onCancel={() => setModal(null)}
        onOk={lancerReaffectation} okText="Réaffecter" confirmLoading={envoi} destroyOnClose>
        {modal?.type === 'reaffecter' && (
          <div className="creg-modal">
            <label>Nouveau livreur</label>
            <Select style={{ width: '100%' }} placeholder="Choisir…" value={modal.livreurId}
              onChange={(v) => setModal((m) => ({ ...m, livreurId: v }))}
              options={livreurs.filter((lv) => lv.id !== modal.ligne.id_livreur)
                .map((lv) => ({ value: lv.id, label: lv.nom || lv.username }))} />
            <label>Motif (obligatoire)</label>
            <Input.TextArea rows={2} value={modal.motif} placeholder="Pourquoi cette réaffectation ?"
              onChange={(e) => setModal((m) => ({ ...m, motif: e.target.value }))} />
          </div>
        )}
      </Modal>

      <Modal open={modal?.type === 'cloturer'} title="Clôturer la commande" onCancel={() => setModal(null)}
        onOk={lancerCloture} okText="Clôturer" confirmLoading={envoi} destroyOnClose>
        {modal?.type === 'cloturer' && (
          <div className="creg-modal">
            <p className="creg-note">Les paires non livrées reviennent au magasin.
              Avec au moins une vente → la commande reste « Livrée » ; sans aucune vente → « Annulée »
              (bloqué si un acompte n'est pas soldé).</p>
            <label>Motif (obligatoire)</label>
            <Input.TextArea rows={2} value={modal.motif} placeholder="Pourquoi cette clôture ?"
              onChange={(e) => setModal((m) => ({ ...m, motif: e.target.value }))} />
          </div>
        )}
      </Modal>

      <AssistantConfusion
        open={!!assistant}
        idCommande={assistant?.idCommande}
        onClose={() => { setAssistant(null); chargerDossiers(); }}
        onDone={() => {
          setAssistant(null);
          chargerDossiers();
          if (fiche) chargerFiche(fiche.commande.id_commande);
        }}
      />
    </div>
  );
};

export default CentreRegularisation;
